/**
 * Multi-step pre-registration form: step machine, conditional sections, the
 * percentage rule, and progress persistence (WWW-S035).
 *
 * Grouping lives in the MARKUP, not here and not in Salesforce: each step is an
 * element carrying `data-step`, in document order. That satisfies the story's
 * requirement that grouping be adjustable without a Salesforce metadata change,
 * and keeps the repo's rule that the website owns the form's structure while
 * Salesforce owns only the values inside fields.
 *
 * ARCHITECTURAL EXCEPTION, recorded deliberately (Aaron, 2026-08-03): CLAUDE.md
 * locks "JavaScript is progressive enhancement only" and "every page must render
 * complete, readable HTML with JS disabled". A stepped wizard breaks the second
 * for this form. Submission was already an accepted exception — the intake is a
 * JSON endpoint that cannot take a native form POST — so this widens an existing
 * exception rather than opening a new one. With JS off every step still renders
 * (they are only hidden by script) and the <noscript> block points to email.
 */

import { HONEYPOT_FIELD } from "./lead-form-guard";

const STORAGE_KEY = "signal:preregistration";
const RADIO = "radio";
const CHECKBOX = "checkbox";

type FieldControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

/** Steps in document order. */
export function readSteps(form: HTMLElement): HTMLElement[] {
  return Array.from(form.querySelectorAll<HTMLElement>("[data-step]"));
}

/**
 * Show one step and hide the rest. Steps are hidden but never disabled — a
 * multi-step form still has to submit every step's answers.
 */
export function showStep(form: HTMLElement, index: number): void {
  const steps = readSteps(form);
  steps.forEach((step, i) => {
    step.hidden = i !== index;
  });
  updateProgress(form, index, steps.length);
}

function updateProgress(form: HTMLElement, index: number, total: number): void {
  const progress = form.querySelector<HTMLElement>("[data-step-progress]");
  if (!progress) return;
  progress.textContent = `Step ${index + 1} of ${total}`;
  progress.setAttribute("aria-valuenow", String(index + 1));
  progress.setAttribute("aria-valuemax", String(total));
}

/**
 * Enabled, named controls inside a container. Disabled ones are excluded because
 * a hidden conditional section must neither validate nor submit.
 */
function controlsIn(container: HTMLElement): FieldControl[] {
  return Array.from(container.querySelectorAll<FieldControl>("[name]")).filter(
    (c) => !c.disabled,
  );
}

/**
 * Validate one step using native constraint validation and surface the first
 * problem. Returns true when the visitor may advance.
 *
 * Native validity is deliberate: `required`, `type="email"` and `maxlength` are
 * already on the markup — partly from live Salesforce metadata — so
 * re-implementing those rules here would let the two drift apart.
 */
export function validateStep(step: HTMLElement): boolean {
  // Clear our own custom messages FIRST. `setCustomValidity` is sticky: it keeps a
  // control invalid until explicitly reset, so a stale message would make the
  // native loop below fail on the old complaint and return before the rule was
  // ever recomputed. That is the bug where correcting 50/25/25 still showed
  // "they currently total 160%" and would not let the visitor past.
  clearCustomMessages(step);

  for (const control of controlsIn(step)) {
    if (!control.checkValidity()) {
      control.reportValidity();
      return false;
    }
  }
  // Rules that span several controls, or that check a value against a list, can't
  // be expressed as native validity on one control.
  return percentagesValid(step) && listValuesValid(step);
}

/** Reset every message this module sets, so each validation pass starts clean. */
function clearCustomMessages(scope: HTMLElement): void {
  for (const input of scope.querySelectorAll<HTMLInputElement>(
    "[data-percentage], input[list]",
  )) {
    input.setCustomValidity("");
  }
}

/**
 * "Must equal 100% between all three lines." CLIENT-SIDE ONLY — Salesforce does
 * not police it, so a submission bypassing JS could still post any split.
 * Accepted because the wizard is JS-driven anyway (Aaron, 2026-08-03).
 *
 * Only fires once at least one percentage has been entered, so an untouched step
 * is never reported invalid.
 */
export function percentagesValid(scope: HTMLElement): boolean {
  const inputs = Array.from(
    scope.querySelectorAll<HTMLInputElement>("[data-percentage]"),
  ).filter((i) => !i.disabled);
  if (inputs.length === 0) return true;

  const entered = inputs.filter((i) => i.value.trim() !== "");
  if (entered.length === 0) return true;

  const total = entered.reduce((sum, i) => sum + (Number(i.value) || 0), 0);
  // Clear all three before deciding, so a message never outlives the numbers that
  // produced it.
  for (const input of inputs) input.setCustomValidity("");
  if (total === 100) return true;

  const first = inputs[0];
  first.setCustomValidity(
    `The three percentages must add up to 100%. They currently total ${total}%.`,
  );
  first.reportValidity();
  return false;
}

/**
 * Validate a free-text input against the options of the `<datalist>` it points at
 * (WWW-S035). Year of formation is a text input rather than a 127-option select —
 * typing "1998" beats scrolling — but the Salesforce field is a restricted
 * picklist, so an unlisted value would be parked and silently lost. Checking here
 * turns that into a message the visitor can act on.
 *
 * Matching accepts either the option's value or its visible label, and is
 * case-insensitive, mirroring how the intake's `matchPicklist` behaves.
 */
export function listValuesValid(scope: HTMLElement): boolean {
  const inputs = Array.from(
    scope.querySelectorAll<HTMLInputElement>("input[list]"),
  ).filter((i) => !i.disabled && i.value.trim() !== "");

  for (const input of inputs) {
    const list = input.ownerDocument.getElementById(
      input.getAttribute("list") ?? "",
    );
    if (!(list instanceof HTMLDataListElement) || list.options.length === 0) {
      continue; // no options to check against — don't invent a failure
    }
    const typed = input.value.trim().toLowerCase();
    const match = Array.from(list.options).some(
      (o) =>
        o.value.toLowerCase() === typed ||
        (o.textContent ?? "").trim().toLowerCase() === typed,
    );
    if (!match) {
      input.setCustomValidity(
        `Please choose one of the listed options. "${input.value.trim()}" isn't one of them.`,
      );
      input.reportValidity();
      return false;
    }
    input.setCustomValidity("");
  }
  return true;
}

/**
 * Show or hide a conditional section, and disable its controls when hidden.
 *
 * Disabling is load-bearing twice over: disabled controls are excluded from
 * `collectPayload`, so a stale value typed before the visitor changed their mind
 * is never submitted; and they are skipped by native validation, so a hidden
 * required field cannot silently block the whole form.
 */
export function setConditional(
  form: HTMLElement,
  name: string,
  shown: boolean,
): void {
  const section = form.querySelector<HTMLElement>(
    `[data-conditional="${name}"]`,
  );
  if (!section) return;
  section.hidden = !shown;
  for (const control of section.querySelectorAll<FieldControl>("[name]")) {
    control.disabled = !shown;
  }
}

/**
 * Wire conditional sections: a control marked `data-controls="<name>"` toggles
 * `[data-conditional="<name>"]`. `data-show-when` names the value that REVEALS
 * the section (default "no"), so the markup reads the way the design does — "Is
 * your mailing address the same as your physical address?" Yes hides, No reveals.
 */
export function initConditionals(form: HTMLElement): void {
  for (const trigger of form.querySelectorAll<HTMLInputElement>(
    "[data-controls]",
  )) {
    const name = trigger.dataset.controls;
    if (!name) continue;
    const showWhen = (trigger.dataset.showWhen ?? "no").toLowerCase();
    const isRadio = trigger.type === RADIO;

    const sync = (): void => {
      // Only the member whose value matches drives the section, so the "Yes"
      // radio's handler does not fight the "No" radio's.
      if (isRadio && trigger.value.toLowerCase() !== showWhen) return;
      setConditional(form, name, trigger.checked);
    };

    if (isRadio) {
      // A radio only fires `change` when it BECOMES checked, so the revealing
      // member never hears about being deselected. Listen on the whole group.
      for (const member of form.querySelectorAll<HTMLInputElement>(
        `input[type="radio"][name="${trigger.name}"]`,
      )) {
        member.addEventListener("change", sync);
      }
    } else {
      trigger.addEventListener("change", sync);
    }
    sync(); // reflect whatever the markup ships as the default
  }
}

/**
 * Drop our custom message the moment the visitor edits the field, so the browser
 * stops showing an assertion that is no longer true. Without this the bubble from
 * a failed check hangs around while the numbers underneath it are being corrected.
 */
function initLiveValidityReset(form: HTMLElement): void {
  for (const input of form.querySelectorAll<HTMLInputElement>(
    "[data-percentage], input[list]",
  )) {
    input.addEventListener("input", () => input.setCustomValidity(""));
  }
}

// --- Progress persistence ----------------------------------------------------

/**
 * Persist answers so abandoning on step 5 does not discard everything (Aaron,
 * 2026-08-03). Values ride in sessionStorage, never the URL, so no personal data
 * reaches browser history or server logs. Cleared on successful submission.
 */
export function saveProgress(form: HTMLFormElement, storage: Storage): void {
  const state: Record<string, unknown> = {};
  for (const control of form.querySelectorAll<FieldControl>("[name]")) {
    if (!control.name) continue;
    // Never persist the honeypot (WWW-S024): a value written into it once would
    // be restored on every later step and keep dropping the submission for the
    // rest of the session.
    if (control.name === HONEYPOT_FIELD) continue;
    if (control instanceof HTMLInputElement && control.type === RADIO) {
      if (control.checked) state[control.name] = control.value;
      continue;
    }
    if (control instanceof HTMLInputElement && control.type === CHECKBOX) {
      state[control.name] = control.checked;
      continue;
    }
    if (control.value !== "") state[control.name] = control.value;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable (private mode, quota) — progress just isn't kept */
  }
}

export function restoreProgress(form: HTMLFormElement, storage: Storage): void {
  let state: Record<string, unknown>;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return;
    state = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return; // corrupt or unreadable — start clean rather than throw
  }
  for (const [name, value] of Object.entries(state)) {
    for (const control of form.querySelectorAll<FieldControl>(
      `[name="${name}"]`,
    )) {
      if (control instanceof HTMLInputElement && control.type === RADIO) {
        control.checked = control.value === value;
      } else if (
        control instanceof HTMLInputElement &&
        control.type === CHECKBOX
      ) {
        control.checked = value === true;
      } else if (typeof value === "string") {
        control.value = value;
      }
    }
  }
}

export function clearProgress(storage: Storage): void {
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clean up */
  }
}

// --- Wiring ------------------------------------------------------------------

export interface StepsController {
  current: () => number;
  goTo: (index: number) => void;
  next: () => void;
  back: () => void;
}

/**
 * Wire the wizard: step navigation, conditional sections, restore/save of
 * in-progress answers. Returns null on pages without steps, so the single-page
 * form — and any future embed of this component on another page — stays valid.
 */
export function initSteps(
  form: HTMLFormElement,
  opts: { storage?: Storage } = {},
): StepsController | null {
  const steps = readSteps(form);
  if (steps.length === 0) return null;

  const storage = opts.storage;
  initConditionals(form);
  initLiveValidityReset(form);
  if (storage) restoreProgress(form, storage);

  let index = 0;
  showStep(form, index);

  const goTo = (next: number): void => {
    index = Math.min(Math.max(next, 0), steps.length - 1);
    showStep(form, index);
    steps[index].scrollIntoView?.({ block: "start" });
    controlsIn(steps[index])[0]?.focus?.();
  };

  form.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const nav = target.closest<HTMLElement>("[data-step-nav]");
    if (!nav) return;
    event.preventDefault();

    if (nav.dataset.stepNav === "next") {
      if (!validateStep(steps[index])) return;
      if (storage) saveProgress(form, storage);
      goTo(index + 1);
    } else {
      // Back never validates: a visitor must always be able to retreat and fix
      // something, and their entries are left intact.
      if (storage) saveProgress(form, storage);
      goTo(index - 1);
    }
  });

  return {
    current: () => index,
    goTo,
    next: () => goTo(index + 1),
    back: () => goTo(index - 1),
  };
}
